import { useMemo, type CSSProperties, type ReactElement } from 'react';
import type { TimelineSpan } from '../../api/admin.js';
import { kindColor } from '../../lib/spanKind.js';
import { buildTree, flattenTree, timelineRange } from './buildTree.js';
import { formatDurationMs, nanoToUsdCompact } from '../../lib/format.js';
import { preferRawCost } from '../../lib/cost-display.js';

export type RowVisibility = 'visible' | 'dim' | 'hidden';

interface WaterfallProps {
  spans: ReadonlyArray<TimelineSpan>;
  selectedId: string | null;
  onSelect: (spanId: string) => void;
  /** Returns visibility for each span; defaults to 'visible'. */
  rowVisibility?: (span: TimelineSpan) => RowVisibility;
}

const MIN_BAR_PCT = 0.4;
const COST_BADGE_GAP_PCT = 0.4;

export function Waterfall({
  spans,
  selectedId,
  onSelect,
  rowVisibility,
}: WaterfallProps): ReactElement {
  const rows = useMemo(() => flattenTree(buildTree(spans)), [spans]);
  const range = useMemo(() => timelineRange(spans), [spans]);
  const ticks = useMemo(() => computeTicks(range.durationMs), [range.durationMs]);

  if (rows.length === 0) {
    return (
      <div className="empty" data-testid="waterfall-empty">
        No hay spans persistidos para esta sesión.
      </div>
    );
  }

  return (
    <div className="waterfall-wrap" data-testid="waterfall">
      <div className="wf-axis" role="presentation">
        <div className="wf-axis-name">Span</div>
        <div className="wf-axis-track">
          {ticks.map((t) => (
            <div key={t.label} className="wf-axis-tick" style={{ left: `${t.pct}%` }}>
              <span>{t.label}</span>
            </div>
          ))}
        </div>
        <div className="wf-axis-dur">Duration</div>
      </div>
      {rows.map(({ span, depth }) => {
        const visibility = rowVisibility ? rowVisibility(span) : 'visible';
        if (visibility === 'hidden') return null;
        const startMs = new Date(span.startedAt).getTime();
        const offsetPct = clampPct(((startMs - range.startMs) / range.durationMs) * 100);
        const widthPct = clampPct(
          Math.max(MIN_BAR_PCT, (span.durationMs / range.durationMs) * 100),
        );
        const badgeLeftPct = clampPct(offsetPct + widthPct + COST_BADGE_GAP_PCT, 92);
        const isSelected = selectedId === span.id;
        const isStriped = span.sampleReason === 'random';
        const isError = span.isError;
        const displayCost = preferRawCost(span.providerCostNano, span.costNano);
        const showCostBadge =
          typeof displayCost === 'string' && displayCost !== '0';
        const barClasses = ['wf-bar'];
        if (isStriped) barClasses.push('striped');
        if (isError) barClasses.push('has-error');
        const barStyle: CSSProperties = {
          left: `${offsetPct}%`,
          width: `${widthPct}%`,
          background: kindColor(span.kind),
          ['--bar-color' as never]: kindColor(span.kind),
        };

        const rowClasses = ['wf-row'];
        if (isSelected) rowClasses.push('is-selected');
        if (visibility === 'dim') rowClasses.push('dim');

        return (
          <div
            key={span.id}
            className={rowClasses.join(' ')}
            onClick={() => onSelect(span.id)}
            data-testid={`wf-row-${span.id}`}
            data-kind={span.kind}
          >
            <div className="wf-name" title={`${span.kind} · ${span.name}`}>
              {Array.from({ length: depth }).map((_, i) => (
                <span key={i} className="wf-indent" aria-hidden="true" />
              ))}
              <span
                className="kdot"
                style={{ background: kindColor(span.kind) }}
                aria-hidden="true"
              />
              <span className="tname mono">{span.name}</span>
              <span className="nkind">
                {span.kind}
                {isStriped ? ' · sampled' : ''}
              </span>
            </div>
            <div className="wf-track" aria-hidden="true">
              <div className={barClasses.join(' ')} style={barStyle} />
              {isError ? (
                <div className="wf-err-badge" style={{ left: `${badgeLeftPct}%` }}>
                  !
                </div>
              ) : null}
              {showCostBadge ? (
                <div
                  className="wf-cost-badge"
                  style={{ left: `${clampPct(badgeLeftPct + (isError ? 2.4 : 0), 92)}%` }}
                >
                  {nanoToUsdCompact(displayCost)}
                </div>
              ) : null}
            </div>
            <div className={`wf-dur${isError ? ' is-error' : ''}`}>
              <span className="wf-dur-main">{formatDurationMs(span.durationMs)}</span>
              {span.kind === 'llm' && typeof span.attributes['ttft_ms'] === 'number' ? (
                <>
                  <span className="wf-dur-sep" aria-hidden="true">
                    ·
                  </span>
                  <span className="wf-dur-ttft" title="Time to first token">
                    TTFT {Math.round(span.attributes['ttft_ms'])}ms
                  </span>
                </>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface Tick {
  pct: number;
  label: string;
}

/**
 * Genera ticks de tiempo cada ~step segundos según la duración total.
 * - duration < 10s   → 5 ticks (cada 20%)
 * - duration < 60s   → cada 10s
 * - duration < 600s  → cada 50s
 * - duration ≥ 600s  → cada 100s
 *
 * Devuelve ticks como porcentaje + label legible.
 */
function computeTicks(durationMs: number): Tick[] {
  if (!Number.isFinite(durationMs) || durationMs <= 0) {
    return [{ pct: 0, label: '0' }];
  }
  let stepMs: number;
  if (durationMs < 10_000) {
    stepMs = durationMs / 5;
  } else if (durationMs < 60_000) {
    stepMs = 10_000;
  } else if (durationMs < 600_000) {
    stepMs = 50_000;
  } else {
    stepMs = 100_000;
  }
  const ticks: Tick[] = [];
  for (let t = 0; t < durationMs; t += stepMs) {
    const pct = clampPct((t / durationMs) * 100, 95);
    ticks.push({ pct, label: tickLabel(t) });
    if (ticks.length >= 12) break;
  }
  return ticks;
}

function tickLabel(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds < 10 ? seconds.toFixed(1) : Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds - m * 60);
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

function clampPct(v: number, max = 100): number {
  if (!Number.isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > max) return max;
  return v;
}
